/**
 * The text of an HTML body, read in one left-to-right pass (no chained
 * tag-stripping regexes, so removing one tag can never splice a new one
 * together). Shared by the iManage preview and workflow email reading.
 *
 * @module htmlText
 */

const SKIPPED_ELEMENTS = ["script", "style"] as const;
const SKIPPED_ENDS = { script: /<\/script>/gi, style: /<\/style>/gi } as const;

/**
 * The text of an HTML body, in one left-to-right pass: script and style
 * elements are dropped with their content, `<br>` and the ends of
 * paragraphs, divisions, list items, table rows and headings become line
 * breaks, and every other tag is removed. A scan, not chained tag-stripping
 * regexes, so removing one tag can never splice a new one together. Entities
 * stay encoded for the caller to decode; the result is only ever shown as text.
 */
export function htmlText(html: string): string {
  let out = "";
  let cursor = 0;
  // An element with no closing tag has none further on either: search once.
  const unclosed = new Set<(typeof SKIPPED_ELEMENTS)[number]>();
  while (cursor < html.length) {
    const open = html.indexOf("<", cursor);
    if (open === -1) return out + html.slice(cursor);
    out += html.slice(cursor, open);
    const skipped = SKIPPED_ELEMENTS.find(
      (name) => html.slice(open + 1, open + 1 + name.length).toLowerCase() === name,
    );
    if (skipped && !unclosed.has(skipped)) {
      const end = SKIPPED_ENDS[skipped];
      end.lastIndex = open;
      if (end.exec(html)) {
        cursor = end.lastIndex;
        continue;
      }
      unclosed.add(skipped);
    }
    const close = html.indexOf(">", open + 1);
    if (close === -1) return out + html.slice(open);
    if (close === open + 1) {
      out += "<>";
    } else if (/^<(?:br\s*\/?|\/(?:p|div|li|tr|h[1-6]))>$/i.test(html.slice(open, close + 1))) {
      out += "\n";
    }
    cursor = close + 1;
  }
  return out;
}
